import { Mail, MapPin } from "lucide-react"
import { Separator } from "@workspace/ui/components/separator"
import { useI18n } from "@/lib/i18n"
import { Logo } from "./logo"

const EMAIL = "contact@blumen.solutions"
const X_URL = "https://x.com/blumensolution"

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      className={className}
    >
      <path d="M18.244 2H21.5l-7.55 8.63L22.85 22h-6.96l-5.45-7.13L4.2 22H.94l8.08-9.23L.65 2h7.14l4.93 6.52L18.244 2Zm-2.44 18h1.87L7.28 3.88H5.28l10.525 16.12Z" />
    </svg>
  )
}

export function Footer() {
  const { t } = useI18n()
  const year = new Date().getFullYear()

  const columns = [
    {
      title: t.footer.products,
      links: [
        { label: t.footer.links.rewards, href: "https://blumenrewards.com" },
        { label: t.footer.links.events, href: "/#products" },
        { label: t.footer.links.roadmap, href: "/#products" },
      ],
    },
    {
      title: t.footer.company,
      links: [
        { label: t.footer.links.work, href: "/#work" },
        { label: t.footer.links.mission, href: "/#studio" },
        { label: t.footer.links.principles, href: "/#principles" },
        { label: t.footer.links.contact, href: "mailto:contact@blumen.solutions" },
      ],
    },
    {
      title: t.footer.legal,
      links: [
        { label: t.footer.links.privacy, href: "/privacy" },
        { label: t.footer.links.terms, href: "/terms" },
      ],
    },
  ]

  return (
    <footer className="relative border-t border-border bg-background pt-20 pb-12">
      <div className="mx-auto max-w-[1400px] px-6 md:px-14 xl:px-20">
        <div className="grid grid-cols-12 gap-x-6 gap-y-12">
          {/* Brand blurb */}
          <div className="col-span-12 md:col-span-5">
            <Logo />
            <p className="mt-6 max-w-sm text-[14px] leading-[1.7] text-foreground/65">
              {t.footer.blurb}
            </p>
            <ul className="mt-6 flex items-center gap-5">
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  aria-label={EMAIL}
                  className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.08em] text-foreground/65 transition-colors hover:text-primary"
                >
                  <Mail className="size-3.5" strokeWidth={1.7} aria-hidden />
                  {EMAIL}
                </a>
              </li>
              <li aria-hidden className="h-3 w-px bg-foreground/20" />
              <li>
                <a
                  href={X_URL}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="X (Twitter)"
                  className="inline-flex text-foreground/65 transition-colors hover:text-primary"
                >
                  <XIcon className="size-[14px]" />
                </a>
              </li>
            </ul>
          </div>

          {/* Link columns */}
          {columns.map((col) => (
            <div
              key={col.title}
              className="col-span-6 md:col-span-2 md:col-start-auto"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-foreground/70">
                {col.title}
              </div>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className="text-[13px] text-foreground/70 transition-colors hover:text-primary"
                    >
                      <span className="link-underline">{l.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="hidden md:col-span-1 md:block" aria-hidden />
        </div>

        <Separator className="mt-16 bg-foreground/12" />

        {/* Bottom bar */}
        <div className="eyebrow-sm mt-6 flex flex-col gap-3 text-foreground/65 md:flex-row md:items-center md:justify-between">
          <div>© {year} Blumen Solutions · {t.footer.rights}</div>
          <div className="flex items-center gap-2">
            <MapPin
              className="size-3 text-primary"
              strokeWidth={1.8}
              aria-hidden
            />
            {t.footer.madeIn}
          </div>
        </div>
      </div>
    </footer>
  )
}
